import { ChangeDetectionStrategy, Component, EventEmitter, OnDestroy, OnInit, Output, inject, input, signal } from '@angular/core';

import { OptionGroupType } from '@frontend/sports/betting-offer/feature/fixture-factories';
import { EventModel, EventOptionGroup } from '@frontend/sports/betting-offer/feature/model';
import { EventSubscription } from '@frontend/sports/event-subscription/feature/base-subscription';
import { DetailedEventSubscriptionService } from '@frontend/sports/event-subscription/feature/detailed-event-subscription';
import { GridSubscriptionService } from '@frontend/sports/event-subscription/feature/grid-subscription';
import { LoadingIndicatorService } from '@frontend/vanilla/core';
import produce from 'immer';
import { clone } from 'lodash-es';
import { finalize } from 'rxjs';

import { EventCarouselSkeletonComponent } from '../../event-carousel/event-carousel-skeleton/event-carousel-skeleton.component';
import { EventCarouselComponent } from '../../event-carousel/event-carousel.component';
import { EventCarouselItem } from '../../event-carousel/event-carousel.model';
import { OptionMarketSetData } from '../../event-details-common/event-details-common.models';
import { EventDetailsService } from '../../event-details-common/event-details.service';
import { SheetViewCarouselDataService } from './sheet-view-carousel-data.service';
import { SheetViewCarouselStateService } from './sheet-view-carousel-state.service';

@Component({
    selector: 'ms-sheet-view-carousel',
    templateUrl: './sheet-view-carousel.component.html',
    styleUrl: './sheet-view-carousel.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [EventCarouselComponent, EventCarouselSkeletonComponent],
    providers: [SheetViewCarouselDataService],
})
export class SheetViewCarouselComponent implements OnInit, OnDestroy {
    @Output() setActiveEvent = new EventEmitter<{ event: EventModel; optionGroup: EventOptionGroup; tileIndex?: number }>();

    sourceEvent = input.required<EventModel>();
    sourceOptionGroup = input.required<EventOptionGroup>();
    sourceGroupType = input.required<OptionGroupType>();
    sourceOptionMarketSetData = input.required<OptionMarketSetData>();
    eventCarouselItems = signal<EventCarouselItem[]>([]);
    selectedEventId = signal<string>('');
    showCarousel = signal<boolean>(false);
    isLoading = signal<boolean>(true);

    private eventSubscription: EventSubscription<EventModel>;
    private gridSubscriptions: EventSubscription<EventModel>;

    private sheetViewCarouselDataService = inject(SheetViewCarouselDataService);
    private subscriptionsService = inject(GridSubscriptionService);
    private loadingIndicatorService = inject(LoadingIndicatorService);
    private eventDetailsService = inject(EventDetailsService);
    private detailedSubscriptionService = inject(DetailedEventSubscriptionService);
    private sheetViewCarouselStateService = inject(SheetViewCarouselStateService);

    ngOnInit(): void {
        this.fetchData();
    }

    eventTileClicked({ eventCarouselItem, tileIndex }: { eventCarouselItem: EventCarouselItem; tileIndex: number }): void {
        const { event } = eventCarouselItem;
        if (event.id === this.selectedEventId()) {
            return;
        }

        this.selectedEventId.set(event.id);

        if (this.eventSubscription) {
            this.eventSubscription.unsubscribe();
        }

        if (event.id == this.sourceEvent().id) {
            this.setActiveEvent.emit({ event: this.sourceEvent(), optionGroup: this.sourceOptionGroup(), tileIndex });
            return;
        }

        const loadingIndicatorHandler = this.loadingIndicatorService.start();
        const marketGroupItemIds = this.sourceOptionGroup().detailedGrouping.marketGroupItemId;

        this.sheetViewCarouselDataService.getFixture(event, marketGroupItemIds, this.sourceGroupType()).subscribe((events) => {
            const eventDetails = events?.[0];
            loadingIndicatorHandler?.done();

            if (!eventDetails) {
                this.setActiveEvent.emit({
                    event,
                    optionGroup: this.sheetViewCarouselDataService.getEmptyOptionGroup(this.sourceOptionGroup()),
                    tileIndex,
                });
                return;
            }

            this.emitData(eventDetails, tileIndex);
            this.setSubscription(eventDetails);
        });
    }

    private fetchData(): void {
        this.sheetViewCarouselDataService
            .getFixtures(this.sourceEvent(), this.sourceOptionGroup())
            .pipe(finalize(() => this.isLoading.set(false)))
            .subscribe({
                next: (eventCarouselItems: EventCarouselItem[] | undefined) => {
                    if (eventCarouselItems) {
                        this.selectedEventId.set(this.sourceEvent().id);
                        this.setEventList(eventCarouselItems);
                    }
                },
            });
    }

    private setEventList(eventCarouselItems: EventCarouselItem[]): void {
        this.eventCarouselItems.update((oldEventCarouselItems) => [...oldEventCarouselItems, ...eventCarouselItems]);

        if (this.eventCarouselItems().length > 1) {
            this.setGridSubscriptions(eventCarouselItems.map((item) => item.event));
            this.handleCarouselVisibility();
        }
    }

    private handleCarouselVisibility(): void {
        if (!this.showCarousel()) {
            this.showCarousel.set(true);
            this.sheetViewCarouselStateService.setShowEventCarousel(this.showCarousel());
        }
    }

    private updateEvents(updatedEvent: EventModel): void {
        const eventList = produce(this.eventCarouselItems(), (draft: EventCarouselItem[]) => this.findAndUpdateEvent(updatedEvent, draft));
        this.eventCarouselItems.set(eventList);
    }

    private findAndUpdateEvent(event: EventModel, draft: EventCarouselItem[]): void {
        const oldEventIndex = draft.findIndex((e) => e.event.id === event.id);
        if (oldEventIndex >= 0) {
            draft[oldEventIndex].event = clone(event);
        }
    }

    private updateEvent(updatedEvent: EventModel): void {
        if (updatedEvent.id === this.selectedEventId()) {
            this.emitData(updatedEvent);
        }
    }

    private emitData(event: EventModel, tileIndex?: number): void {
        // need this filtering for data via websocket
        event.optionGroups = (event.optionGroups || []).filter(
            (op) =>
                op.detailedGrouping?.marketGroupItemId === this.sourceOptionGroup().detailedGrouping?.marketGroupItemId &&
                op.detailedGrouping?.marketOrder === this.sourceOptionGroup().detailedGrouping?.marketOrder,
        );

        const optionGroups = this.eventDetailsService.prepareEventOptionGroups({
            event: event,
            ...this.sourceOptionMarketSetData(),
        });

        const updatedOptionGroups = optionGroups?.[0] || this.sheetViewCarouselDataService.getEmptyOptionGroup(this.sourceOptionGroup());
        this.setActiveEvent.emit({ event, optionGroup: updatedOptionGroups, tileIndex });
    }

    private setSubscription(event: EventModel): void {
        this.eventSubscription = this.detailedSubscriptionService.subscribe([
            {
                event: event,
                callback: (updatedEvent: EventModel) => this.updateEvent(updatedEvent),
            },
        ]);
    }

    private setGridSubscriptions(eventList: EventModel[]): void {
        const eventSubs = this.subscriptionsService.subscribe(
            eventList.map((event) => ({
                event,
                callback: (updatedEvent) => this.updateEvents(updatedEvent),
            })),
        );

        if (this.gridSubscriptions) {
            this.gridSubscriptions.add(eventSubs);
        } else {
            this.gridSubscriptions = eventSubs;
        }
    }

    ngOnDestroy() {
        this.gridSubscriptions?.unsubscribe();
        this.eventSubscription?.unsubscribe();
        this.sheetViewCarouselStateService.setShowEventCarousel(false);
    }
}
